import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import {
    getConnection,
    type MailboxConnectionResponse,
    type MailboxLastRelease,
    type MailboxNextStep,
} from './mailboxApi';

/**
 * The connection read for `/email/mailbox` and `/email`.
 *
 * It reads through TanStack Query (installed by F5-d WI-1.2, lead decision 2026-09-24), which
 * replaced the small in-repo hook F20-a built here (its lead's option C, 2026-09-22). As that hook's
 * header promised, the move rewrote this file and no component: the returned shape below is
 * unchanged, so `MailboxRoute` and `EmailRoute` read it exactly as before.
 *
 * **The connection check (F20-i spec FR-16).** The read is `GET /connection`'s `200` check: the two settled
 * states carry the server's `nextStep` and `lastRelease` beside the connection (or its absence), and the
 * app-wide `MailboxConnectProvider` observes this same key, so the check runs on every CRM load and again
 * whenever the mailbox screen or the Email screen mounts — TanStack re-reads a stale query on mount, and
 * `queryClient.ts` sets no `staleTime`.
 *
 * **What is contained here and what is not.** The READ path is contained. The MUTATIONS are not —
 * `disconnect()` and `stubConnect()` are called from the components that own those buttons, which
 * then call this hook's `refetch`; moving them onto `useMutation` is owed (harness `.forge/owed.md`).
 *
 * **Both routes read one cached copy.** `MailboxRoute` and `EmailRoute` are flat siblings that never
 * mount together; each mounts its own observer of the one query key, so the screen the user moves
 * to shows the last read at once and re-reads it (published as `refreshing`, BUG-026's rule).
 */

/** The query key of the connection read. */
const MAILBOX_CONNECTION_KEY = ['mailbox', 'connection'] as const;

/** Re-run the read and republish the result. Never rejects: a failure lands in the `error` state. */
export type RefetchConnection = () => Promise<void>;

/**
 * The hook's fixed return shape, written as a discriminated union so `connection` and `error` are
 * *checked* against the state rather than asserted at each call site.
 *
 * **`refreshing` is carried by the two settled data states** (BUG-026). A `refetch` after one of them
 * keeps the last result on screen and sets `refreshing: true` until the re-read lands, instead of going
 * back to `loading` — which unmounted the whole panel and dropped keyboard focus to `<body>`. That is
 * TanStack Query's own behaviour: `data` is kept while `isFetching`. `loading` and `error` hold no data
 * to keep: the first read starts at `loading`, and a retry from `error` passes through `loading` again.
 * TanStack does that itself only for a query that has never succeeded; a query that failed after a
 * success keeps `status: 'error'` (and its old data) while it re-reads, so the hook maps an `error`
 * that is fetching to `loading` too — the ErrorPanel's retry then shows the read is under way. A
 * failed re-read publishes `error`.
 *
 * **Every state also carries the read's error counters** (TanStack's `errorUpdateCount` and
 * `errorUpdatedAt`), so a caller can keep a failure on screen through its *Retry* with
 * `useRetryHold` (`src/lib/hooks/useRetryHold.ts`): a retry publishes `loading`, and only a new
 * failure moves the counters.
 */
export type MailboxConnectionQuery = MailboxConnectionErrorUpdates &
    MailboxConnectionSettles &
    (
        | { readonly state: 'loading'; readonly refetch: RefetchConnection }
        | {
              readonly state: 'not-connected';
              readonly nextStep: MailboxNextStep;
              readonly lastRelease: MailboxLastRelease | null;
              readonly refreshing: boolean;
              readonly refetch: RefetchConnection;
          }
        | {
              readonly state: 'connected';
              readonly connection: MailboxConnectionResponse;
              readonly nextStep: MailboxNextStep;
              readonly lastRelease: MailboxLastRelease | null;
              readonly refreshing: boolean;
              readonly refetch: RefetchConnection;
          }
        | { readonly state: 'error'; readonly error: unknown; readonly refetch: RefetchConnection }
    );

/**
 * How many times the read has settled, with data or with a failure (the query state's `dataUpdateCount` plus
 * `errorUpdateCount`; the observer result carries no data count). Read on every render the hook makes, and a
 * settle always renders it (`isFetching` goes `false`). A re-read whose answer did not change still moves it,
 * so the app-wide connect owner can tell the check it is waiting for from the one already on screen (F20-i
 * WI-1.3 plan `## Notes`, the lead's decision of 2026-11-02).
 */
export interface MailboxConnectionSettles {
    readonly settledCount: number;
}

/** The read's error counters (`MailboxConnectionQuery`'s note). */
export interface MailboxConnectionErrorUpdates {
    /** How many times the read has failed; a new failure moves it. */
    readonly errorUpdateCount: number;
    /** When the read last failed, in epoch milliseconds, or `0` when it never has. */
    readonly errorUpdatedAt: number;
}

export interface MailboxConnectionOptions {
    /**
     * `false` sends nothing: the app-wide connect owner passes whether the user holds `email.manage`, so
     * no other user ever calls the check (F20-i contract § Endpoints, "When the client calls the check").
     * Every other observer leaves it on. The query reads while any observer of the key is enabled, so the
     * moment a `/me` re-read grants the key the check is read (F20-i AC-60). Defaults to `true`.
     */
    readonly enabled?: boolean;
}

export function useMailboxConnection({
    enabled = true,
}: MailboxConnectionOptions = {}): MailboxConnectionQuery {
    // Every refusal rejects inside `getConnection` and becomes the error state, which renders an
    // ErrorPanel with a retry rather than an empty state (the repo's "three states are not two" rule).
    // A `404` is one of them (F20-i spec FR-16): "no mailbox" is the check's `200` with `connection: null`. The
    // error object is published so the route can tell a 403 from a generic failure.
    const query = useQuery({ queryKey: MAILBOX_CONNECTION_KEY, queryFn: getConnection, enabled });
    const settles = useQueryClient().getQueryState(MAILBOX_CONNECTION_KEY);
    const { refetch: refetchQuery } = query;

    const refetch = useCallback<RefetchConnection>(async () => {
        // TanStack's `refetch` resolves with the result rather than rejecting on a failure, so the
        // promise this returns never rejects either.
        await refetchQuery();
    }, [refetchQuery]);

    const counters: MailboxConnectionErrorUpdates & MailboxConnectionSettles = {
        errorUpdateCount: query.errorUpdateCount,
        errorUpdatedAt: query.errorUpdatedAt,
        settledCount: (settles?.dataUpdateCount ?? 0) + (settles?.errorUpdateCount ?? 0),
    };
    if (query.status === 'pending' || (query.status === 'error' && query.isFetching)) {
        return { ...counters, state: 'loading', refetch };
    }
    if (query.status === 'error') {
        return { ...counters, state: 'error', error: query.error, refetch };
    }
    const read = query.data;
    return read.kind === 'connected'
        ? {
              ...counters,
              state: 'connected',
              connection: read.connection,
              nextStep: read.nextStep,
              lastRelease: read.lastRelease,
              refreshing: query.isFetching,
              refetch,
          }
        : {
              ...counters,
              state: 'not-connected',
              nextStep: read.nextStep,
              lastRelease: read.lastRelease,
              refreshing: query.isFetching,
              refetch,
          };
}
